import React, { useState, useEffect, useCallback, useMemo } from "react";
import apiService from "../../services/api.service";
import "../../i18n/config";
import useMobileScreen from "../useMobileScreen";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "../../hooks/useTranslation";
import BrandFilter from "./BrandFilter";
import PropertyFilter from "./PropertyFilter";
import CategoryFilter from "./CategoryFilter";
import MobilePropertyBrandFilter from "./MobilePropertyBrandFilter";

const FilterDropdownComponent = ({ products, lang, setProductsFiltered, productsFiltered }) => {
    const { t } = useTranslation();
    const chain = useSelector((state) => state.chain);
    const isMobile = useMobileScreen();
    const location = useLocation();

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [brands, setBrands] = useState([]);
    const [selectedBrand, setSelectedBrand] = useState(null);
    const [selectedBrandName, setSelectedBrandName] = useState(null);
    const [currentChainBrand, setCurrentChainBrand] = useState(null);

    const [cities, setCities] = useState([]);
    const [showCityLabel, setShowCityLabel] = useState(false);
    const [properties, setProperties] = useState([]);
    const [propertiesFiltered, setPropertiesFiltered] = useState([]);   //filtered by brands, property has products
    const [selectedProperty, setSelectedProperty] = useState(null);
   
    //query strings
    const propertyIdQueryParam = new URLSearchParams(location.search).get("propertyId") || 0;
    const categoryIdQueryParam = Number(new URLSearchParams(location.search).get("catid")) || null;
    

    //reset filters
    useEffect(() => {
        if (!chain) return;
        setSelectedBrand(null);
        setSelectedBrandName(null);
        setSelectedProperty(null);
        setSelectedCategory(null);
    }, [chain?.chainId]);

    //get datas & sort by sortorder
    useEffect(() => {
        if (!chain) return;

        const fetchData = async () => {
            // Categories (sync, no API call)
            if (chain.filterByCategory) {
                const categories = products?.flatMap(product => product.categories || []);
                const uniqueCategories = [...new Set(categories?.map(c => c.productCategoryId))]
                    .map(id => categories.find(c => c.productCategoryId === id));

                if (!!categoryIdQueryParam) {
                    let selected = uniqueCategories.find(c => c.productCategoryId == categoryIdQueryParam)?.productCategoryId || 'all';
                    setSelectedCategory("category-" + selected);
                }

                setCategories(uniqueCategories);
            }

            // Parallel API calls with Promise.all
            const [brandsRes, citiesRes, propertiesRes] = await Promise.allSettled([
                apiService.getBrands(chain.chainId),
                !cities.length ? apiService.getCities(chain.chainId) : Promise.resolve(null),
                apiService.getProperties(chain.chainId)
            ]);

            // Handle brands
            if (brandsRes.status === 'fulfilled') {
                const { data } = brandsRes.value;
                if (data?.data && !data.errors) {
                    setBrands(data.data.filter(b => b.isActive).sort((a, b) => a.sortOrder - b.sortOrder));
                    setCurrentChainBrand(data.data.find(brand => brand.brandId == chain?.brandId && brand.isActive));
                }
            } else {
                console.log(brandsRes.reason);
            }

            // Handle cities
            if (citiesRes.status === 'fulfilled' && citiesRes.value?.data?.data) {
                const cities = citiesRes.value.data.data.sort((a, b) => {
                    const aSort = a.translationsChainCity.find(t => t.languageIsoCode === lang)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
                    const bSort = b.translationsChainCity.find(t => t.languageIsoCode === lang)?.sortOrder ?? Number.MAX_SAFE_INTEGER;

                    return aSort - bSort;
                });
                setCities(cities);
            } else if (citiesRes.status === 'rejected') {
                console.log(citiesRes.reason);
            }

            // Handle properties
            if (propertiesRes.status === 'fulfilled') {
                const { data } = propertiesRes.value;
                if (data?.data) {

                    const properties = data?.data.filter(p => p.isActive).sort((a, b) => {
                        const aSort = a.translations.find(t => t.languageIsoCode === lang)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
                        const bSort = b.translations.find(t => t.languageIsoCode === lang)?.sortOrder ?? Number.MAX_SAFE_INTEGER;

                        return aSort - bSort;
                    });

                    setProperties(properties);
                    sessionStorage.setItem("properties", JSON.stringify(properties));

                    if (!!propertyIdQueryParam) {
                        setSelectedProperty("property-" + propertyIdQueryParam);
                    }

                    const propertiesWithProducts = properties.filter(property =>
                        products.some(product =>
                            product.propertyId === property.propertyId || product.propertyId == -1
                        )
                    );
                    setPropertiesFiltered(products ? properties : propertiesWithProducts);
                }
            } else {
                console.log(propertiesRes.reason);
            }
        };

        fetchData();
    }, [products, chain]);

    //apply filters
    useEffect(() => {
        applyFilters();
    }, [selectedCategory, selectedProperty, selectedBrand, products, properties]);

    const applyFilters = () => {
        let filtered = products;

        if (selectedCategory || categoryIdQueryParam) {
            let selectedId = selectedCategory?.toString().replace("category-", "") ?? categoryIdQueryParam;
            filtered = filtered.filter(pr =>
                pr.categories.some(c => c.productCategoryId == selectedId || selectedId == "all")
            );
        }
        if(selectedProperty || propertyIdQueryParam){
            let selectedId = selectedProperty?.toString().replace("property-", "") ?? propertyIdQueryParam;
            filtered = filtered.filter(pr => pr.propertyId == selectedId || pr.propertyId == '-1' || selectedId == "all");
        }
        if(selectedBrand){
            let selectedId = selectedBrand.replace("brand-", "");
            let brandProperties = properties.filter(p => p.brandId == selectedId || selectedId == "all");
            let brandPropertiesIds = brandProperties.map(p => p.propertyId);
            setPropertiesFiltered(brandProperties);
            filtered = filtered.filter(pr => brandPropertiesIds.includes(pr.propertyId) || pr.propertyId == '-1');
        }

        setProductsFiltered(filtered);
    };

    return <div className="filter">
        {
            (brands.length > 1 || (!!cities.length && propertiesFiltered.length > 1) || !!categories && categories.length > 1) &&
            <div className="filter-label">{t("FilterBy")}</div>
        }
        {
            ( isMobile && (brands.length > 1 || (!!cities.length && propertiesFiltered.length > 1)) ) &&
            <MobilePropertyBrandFilter 
                brands={brands}
                lang={lang}
                properties={properties}
                propertiesFiltered={propertiesFiltered}
                cities={cities}
                currentChainBrand={currentChainBrand}
                selectedBrand={selectedBrand}
                selectedBrandName={selectedBrandName}
                selectedProperty={selectedProperty}
                setPropertiesFiltered={setPropertiesFiltered}
                setSelectedBrand={setSelectedBrand}
                setSelectedBrandName={setSelectedBrandName}
                setSelectedCategory={setSelectedCategory}
                setSelectedProperty={setSelectedProperty}
                products={products}
            />
        }
        {
            (!isMobile && brands.length > 1) &&
            <BrandFilter
                brands={brands}
                chainBrand={currentChainBrand}
                selectedValue={selectedBrand}
                setSelectedName={setSelectedBrandName}
                setSelected={setSelectedBrand}
                lang={lang}
            />
        }
        {
           ( !isMobile && (!!cities.length && propertiesFiltered.length > 1) ) &&
            <PropertyFilter
                properties={propertiesFiltered}
                selectedValue={selectedProperty}
                setSelected={setSelectedProperty}
                lang={lang}
                brands={brands}
                selectedBrandName={selectedBrandName}
                cities={cities}
            />
        }
        {
            (!!categories && categories.length > 1) &&
            <CategoryFilter
                lang={lang}
                products={products}
                categories={categories}
                setSelected={setSelectedCategory}
                selectedValue={selectedCategory ?? ""}
                selectedProperty={selectedProperty}
                selectedBrand={selectedBrand}
                properties={properties}
            />
        }
    </div>
}

export default FilterDropdownComponent;